import {
  isDataClassificationError,
  isMissingDataGroupError,
  isUnsupportedActionError,
} from 'common/report/queries/utils';
import {renamePropertyValue} from 'common/report/util/properties';
import {LoadingStatus} from 'common/report/util/retention/constants';
import {Segment} from 'common/widgets/query-block/segment-menu/types';
import {
  ErrorResponse,
  isErrorResponse,
  isSegmented,
  KeyedByDate,
  KeyedBySegment,
  RetentionData,
  RetentionQueryResponse,
  SegmentKeyToPath,
  SuccessResponse,
} from 'common/report/data/retention/client';

import {getGroupByName} from './format';

/**
 *
 * Transform the API response format with a multiseg tree to a tree with exactly 3 levels
 * date, flattened segments, retention/addiction data. Also returns an object for retrieving
 * the original segment path from the flattened segment string.
 **/
function flattenSegments(
  response: KeyedByDate<any>,
  segmentGroups: Segment[],
): {responseData: KeyedByDate<any>; segmentKeyToPath: SegmentKeyToPath} {
  function flatten(
    segments: KeyedBySegment<any>,
    depth: number,
    prefix = null,
    pathAccum = [],
  ): {flattened: KeyedBySegment<any>; segmentKeyToPath: SegmentKeyToPath} {
    const flattened = {};
    const segmentKeyToPath = {};
    for (const [segment, data] of Object.entries(segments)) {
      const renamedSegment = renamePropertyValue(segment, getGroupByName(segmentGroups[segmentGroups.length - depth]));
      const key = prefix ? `${prefix} / ${renamedSegment}` : renamedSegment;
      const path = [...pathAccum, renamedSegment];
      if (depth === 1) {
        flattened[key] = data;
        segmentKeyToPath[key] = path;
      } else {
        const {flattened: recursiveFlattened, segmentKeyToPath: recursivePath} = flatten(data, depth - 1, key, path);
        Object.assign(flattened, recursiveFlattened);
        Object.assign(segmentKeyToPath, recursivePath);
      }
    }

    return {flattened, segmentKeyToPath};
  }

  const responseData = {};
  let segmentKeyToPath = {};

  for (const [date, segments] of Object.entries(response)) {
    const {flattened, segmentKeyToPath: path} = flatten(segments, segmentGroups.length);
    responseData[date] = flattened;
    segmentKeyToPath = {...segmentKeyToPath, ...path};
  }

  return {responseData, segmentKeyToPath};
}

export function isNoDataResponse(response: KeyedByDate<any>): boolean {
  if (Object.keys(response).length === 0) {
    return true;
  } else if (Object.keys(response).length === 1) {
    // we return an empty $average row now on empty data.
    return Object.keys(response[Object.keys(response)[0]]).length === 0;
  }

  return false;
}

/** Parse a raw response into AppState, renaming segments as well. */
export function processQueryResponse(
  response: RetentionQueryResponse,
  segmentGroups: Segment[],
): {
  status: LoadingStatus;
  error: ErrorResponse;
  data: RetentionData;
  segmentKeyToPath: SegmentKeyToPath;
  samplingFactor: number;
  computedAt: string;
  metadata?: SuccessResponse['metadata'];
} {
  if (isErrorResponse(response)) {
    let error: ErrorResponse = null;
    let status: LoadingStatus = LoadingStatus.Loading;
    if (isDataClassificationError(response.error)) {
      status = LoadingStatus.DataClassificationError;
      error = {error: response.error};
    } else if (isMissingDataGroupError(response.error)) {
      status = LoadingStatus.MissingMappedDataGroup;
      error = {error: response.error, data: response.data};
    } else if (isUnsupportedActionError(response.statusCode)) {
      status = LoadingStatus.UnsupportedActionError;
      error = {error: response.error, statusCode: response.statusCode};
    } else {
      console.error(`Query returned an error: `, response.message);
      status = LoadingStatus.Error;
      error = {error: response.message};
    }
    return {
      status,
      error,
      data: null,
      segmentKeyToPath: null,
      samplingFactor: null,
      computedAt: null,
    };
  } else {
    let responseData = response.results || response.data;
    let segmentKeyToPath = null;
    if (isSegmented(responseData, segmentGroups?.length > 0)) {
      ({responseData, segmentKeyToPath} = flattenSegments(responseData, segmentGroups));
    }
    return {
      status: isNoDataResponse(responseData) ? LoadingStatus.NoData : LoadingStatus.Complete,
      samplingFactor: response.metadata?.min_sampling_factor || null,
      computedAt: response.computed_at,
      data: responseData,
      segmentKeyToPath,
      metadata: response.metadata,
      error: null,
    };
  }
}
